import { useCallback, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { sharingCalls } from '@/services/case-sharing';
import { CaseParticipant, ShareAnchor } from '@/types/report-request';

/**
 * Who a case is shared with, through its anchor (enquiry, request or
 * report). The list includes shares inherited from above, marked by
 * `source`; only `direct` ones can be removed through this anchor.
 * Everyone who can see the case can read the list; add/remove are for
 * client admins and GTI admins (the API 403s restricted roles, so
 * callers should also hide the controls).
 */
export function useCaseParticipants(anchor: ShareAnchor | undefined) {
  const kind = anchor?.kind;
  const anchorId = anchor?.id;
  const [participants, setParticipants] = useState<CaseParticipant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!kind || !anchorId) {
      setParticipants([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const rows = await sharingCalls(kind).list(anchorId);
      // The hook promises an array to every consumer, so honour that even
      // when the wire doesn't — a null body or an empty 204 would
      // otherwise land in state and crash `participants.map` at render,
      // which is a blank sharing bar rather than a caught error.
      setParticipants(Array.isArray(rows) ? rows : []);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load sharing');
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [kind, anchorId]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  const addParticipant = useCallback(
    async (email: string) => {
      if (!kind || !anchorId) {
        return;
      }
      await sharingCalls(kind).add(anchorId, email);
      await fetchOnce();
    },
    [kind, anchorId, fetchOnce]
  );

  const removeParticipant = useCallback(
    async (participantId: string) => {
      if (!kind || !anchorId) {
        return;
      }
      await sharingCalls(kind).remove(anchorId, participantId);
      await fetchOnce();
    },
    [kind, anchorId, fetchOnce]
  );

  return { participants, isLoading, error, addParticipant, removeParticipant };
}
